import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import { SESSION_STATUSES, BOOKING_FEE_DISPLAY } from '../utils/constants';
import { formatDate, formatTime } from '../utils/timeFormat';
import './PaymentPage.css';

function PaymentPage() {
  const { sessionRequestId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      if (process.env.NODE_ENV === 'development') {
        console.log('PaymentPage: loading session_request for id:', sessionRequestId);
      }
      try {
        const { data, error: err } = await supabase
          .from('session_requests')
          .select(`
            *,
            mentor:profiles!session_requests_mentor_id_fkey(id, first_name, last_name, avatar_url, hourly_rate, sport)
          `)
          .eq('id', sessionRequestId)
          .single();

        if (err || !data) {
          setError('Session not found.');
          setLoading(false);
          return;
        }

        // SECURITY: verify ownership
        if (data.parent_id !== user.id) {
          setError('Unauthorized.');
          setLoading(false);
          return;
        }

        // SECURITY: verify status is accepted
        if (data.status !== SESSION_STATUSES.ACCEPTED) {
          if (data.status === SESSION_STATUSES.PAID) {
            navigate('/dashboard');
            return;
          }
          setError('This session is not ready for payment.');
          setLoading(false);
          return;
        }

        setRequest(data);
      } catch (err) {
        setError(err.message || 'Failed to load session details.');
        if (process.env.NODE_ENV === 'development') {
          console.error('PaymentPage load error:', err.message);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionRequestId, user.id, navigate]);

  const handleProceedToPayment = async () => {
    setPayError('');
    setPaying(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession?.access_token;
      if (!token) throw new Error('Authentication required.');

      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionRequestId,
          parentEmail: authSession?.user?.email,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Payment initialization failed.');
      }

      if (!responseData.url) throw new Error('No checkout URL returned.');

      window.location.href = responseData.url;
    } catch (err) {
      setPayError(err.message || 'Payment failed. Please try again.');
      if (process.env.NODE_ENV === 'development') {
        console.error('PaymentPage payment error:', err.message);
      }
      setPaying(false);
    }
  };

  if (loading) {
    return <div className="app-loading"><div className="app-loading-spinner" /></div>;
  }

  if (error || !request) {
    return (
      <div className="page-container">
        <div className="alert alert-error">{error || 'Session not found.'}</div>
        <Link to="/dashboard" className="btn btn-ghost" style={{ marginTop: '1rem' }}>
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const mentor = request.mentor;

  return (
    <div className="payment-page">
      <div className="payment-page-inner">
      <Link to="/dashboard" className="session-back-link">← Back to Dashboard</Link>

      <div className="payment-layout">
        {/* Session summary */}
        <div className="payment-summary">
          <h2 className="payment-summary-title">Session Summary</h2>

          <div className="payment-mentor-row">
            {mentor?.avatar_url ? (
              <img src={mentor.avatar_url} alt={mentor ? `${mentor.first_name} ${mentor.last_name}` : ''} className="payment-mentor-avatar" />
            ) : (
              <div className="payment-mentor-avatar-placeholder">
                {mentor?.first_name?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div>
              <p className="payment-mentor-name">{mentor ? `${mentor.first_name} ${mentor.last_name}` : ''}</p>
              {mentor?.sport && <span className="sport-tag">{mentor.sport}</span>}
            </div>
          </div>

          <div className="payment-detail-grid">
            <div className="detail-item">
              <div className="detail-key">Sport</div>
              <div className="detail-val">{request.sport}</div>
            </div>
            <div className="detail-item">
              <div className="detail-key">Date</div>
              <div className="detail-val">{formatDate(request.proposed_date)}</div>
            </div>
            <div className="detail-item">
              <div className="detail-key">Time</div>
              <div className="detail-val">{formatTime(request.proposed_time)}</div>
            </div>
            <div className="detail-item">
              <div className="detail-key">Location</div>
              <div className="detail-val">{request.proposed_location}</div>
            </div>
          </div>

          {mentor?.hourly_rate != null && (
            <div className="payment-rate-reminder alert alert-info">
              <strong>Reminder:</strong> Pay {`${mentor.first_name} ${mentor.last_name}`} <strong>${request.duration ? (Number(mentor.hourly_rate) * Number(request.duration)).toFixed(0) : Number(mentor.hourly_rate).toFixed(0)}</strong> in person at your session{request.duration ? ` (${Number(request.duration)} ${Number(request.duration) === 1 ? 'hour' : 'hours'} × $${Number(mentor.hourly_rate).toFixed(0)}/hr)` : ''}. This is separate from the booking fee.
            </div>
          )}
        </div>

        {/* Payment box */}
        <div className="payment-checkout">
          <h2 className="payment-checkout-title">Booking Fee</h2>

          <div className="payment-fee-display">
            <span className="payment-fee-amount">{BOOKING_FEE_DISPLAY}</span>
            <span className="payment-fee-label">one-time booking fee</span>
          </div>

          <ul className="payment-what-you-get">
            <li>✓ Confirms your session with {mentor ? `${mentor.first_name} ${mentor.last_name}` : ''}</li>
            <li>✓ Both you and trainer receive email confirmation</li>
            <li>✓ Session details locked in</li>
          </ul>

          <p className="payment-secure-note">🔒 Secure payment via Stripe</p>

          {payError && <div className="alert alert-error">{payError}</div>}

          <button
            className="payment-btn"
            onClick={handleProceedToPayment}
            disabled={paying}
          >
            {paying ? 'Redirecting to Checkout…' : `Pay ${BOOKING_FEE_DISPLAY} Now`}
          </button>

          <p className="payment-cancel-note">
            <Link to="/dashboard">Cancel — return to dashboard</Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}

export default PaymentPage;
